import { actions, GuardMeta, raise, sendTo } from "xstate"
import * as O from "fp-ts/Option"
import * as F from "fp-ts/function"
import { CheckoutProcessId, PriceRequestV2, QuoteProcessId } from "@ssc-booking/api-contracts"
import { RequestResponseChannel } from "../../../../../_common/machines/data-load-machine"
import { assertEvent, maybeEvent } from "../../../../../_common/xstate-helpers"
import { fareCardByKey } from "../../../read-models/fare"
import { validVsCodeValue, vsMember } from "../../../vs-code"
import { buildFareOptionsLoader, FareOptionsLoaderTypes } from "../data-loading/fare-options-loader-machine"
import {
    buildValidateCheckoutCommand,
    ValidateCheckoutCommandTypes,
} from "../data-loading/validate-checkout-command-machine"
import { buildStartPaymentCommand, StartPaymentCommandTypes } from "../data-loading/start-payment-command-machine"
import { ECommerceEvent, ECommerceStateNodeConfig } from "../e-commerce-machine"
import { buildPastGuestLoader, PastGuestLoaderTypes } from "../data-loading/past-guest-loader-machine"
import {
    buildValidateWebQuoteCommand,
    ValidateWebQuoteCommandTypes,
} from "../data-loading/validate-web-quote-command-machine"
import { buildCheckoutStatusLoader, CheckoutStatusLoaderTypes } from "../data-loading/checkout-status-loader-machine"
import { ECommerceContext } from "../e-commerce-machine-context"
import { isReadyToCheckout } from "../e-commerce-machine-transitions"

export const dataLoadingConfigCommon = (
    websocketClient: RequestResponseChannel,
): ECommerceStateNodeConfig["states"] => ({
    fareOptions: {
        invoke: { id: "fareOptionsLoader", src: buildFareOptionsLoader(websocketClient) },
        on: {
            NO_CHECKOUT_TO_RESTORE: { actions: [raise({ type: "LOAD_FARE_OPTIONS" })] },
            GUESTS_CHANGED: { cond: "isNotCheckingOut", actions: [raise({ type: "LOAD_FARE_OPTIONS" })] },
            LOAD_FARE_OPTIONS: { cond: "isNotCheckingOut", actions: ["loadFareOptions"] },
            FARE_OPTIONS_LOADED: { cond: "isNotCheckingOut", actions: ["setFareCards", "setCruiseInfo"] },
        },
    },
    pastGuest: {
        invoke: { id: "pastGuestLoader", src: buildPastGuestLoader(websocketClient) },
        on: {
            VALIDATE_VS_CODE: { cond: "isNotCheckingOut", actions: ["validateVsCode"] },
        },
    },
    validateWebQuote: {
        invoke: { id: "validateWebQuoteCommand", src: buildValidateWebQuoteCommand(websocketClient) },
        on: {
            VALIDATING_WEB_QUOTE: { cond: "isNotCheckingOut", actions: ["validateWebQuote"] },
        },
    },
    validateCheckout: {
        invoke: { id: "validateCheckoutCommand", src: buildValidateCheckoutCommand(websocketClient) },
        on: {
            VALIDATING_CHECKOUT: { cond: "isNotCheckingOut", actions: ["validateCheckout"] },
        },
    },
    checkoutStatus: {
        invoke: { id: "checkoutStatusLoader", src: buildCheckoutStatusLoader(websocketClient) },
        on: {
            CHECKOUT_VALIDATED: { actions: ["loadCheckoutStatus"] },
            LOAD_EXISTING_CHECKOUT: { actions: ["loadCheckoutStatus"] },
            INVALID_CHECKOUT_FOR_PAYMENT: { actions: ["loadCheckoutStatus"] },
        },
    },
    startPayment: {
        invoke: { id: "startPaymentCommand", src: buildStartPaymentCommand(websocketClient) },
        on: {
            START_PAYMENT: { actions: ["startPayment"] },
        },
    },
})

type Context = ECommerceContext
type Events = ECommerceEvent

export const isNotCheckingOut = (_ctx: Context, _ev: Events, { state }: GuardMeta<Context, Events>) =>
    !isReadyToCheckout(state)

export const dataLoadingOptionsCommon = {
    guards: {
        isNotCheckingOut,
    },
    actions: {
        loadFareOptions: sendTo<Context, Events, FareOptionsLoaderTypes["ActorRef"]>(
            "fareOptionsLoader",
            (ctx): FareOptionsLoaderTypes["Load"] => ({
                type: "LOAD",
                data: {
                    cruiseCode: ctx.cruiseCode,
                    occupancy: ctx.guests,
                    gatewayCode: O.none,
                },
                country: ctx.geoLocation.countryCode,
            }),
        ),

        validateVsCode: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "VALIDATE_VS_CODE")
            return [
                sendTo<Context, Events, PastGuestLoaderTypes["ActorRef"]>(
                    "pastGuestLoader",
                    (ctx): PastGuestLoaderTypes["Load"] => ({
                        type: "LOAD",
                        data: { vsCode: ev.vsCode },
                        country: ctx.geoLocation.countryCode,
                    }),
                ),
            ]
        }),

        validateWebQuote: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "VALIDATING_WEB_QUOTE")
            const selectedSuiteCategory = ctx.selectedSuiteCategory
            const fare = fareCardByKey(ctx, ctx.selectedFare?.key)
            if (!selectedSuiteCategory || !fare) return undefined
            return [
                sendTo<Context, Events, ValidateWebQuoteCommandTypes["ActorRef"]>(
                    "validateWebQuoteCommand",
                    (): ValidateWebQuoteCommandTypes["Load"] => ({
                        type: "LOAD",
                        data: {
                            id: QuoteProcessId.fromUUID(ctx.sessionId),
                            trackingId: ev.trackingData.trackingId,
                            auid: ev.trackingData.auid,
                            captchaSolution: ev.captchaSolution,
                            price: PriceRequestV2.build({
                                cruiseCode: ctx.cruiseCode,
                                fareCode: fare.fare,
                                vsMember: vsMember(ctx.vsCode, fare.options.vs),
                                vsCode: O.none,
                                suiteCategory: selectedSuiteCategory,
                                occupancy: ctx.guests,
                                availabilities: ctx.flowVersion.availabilities,
                                air: fare.air,
                            }),
                            customer: ev.mainGuest,
                            source: ctx.flowVersion.quoteSource,
                        },
                        country: ctx.geoLocation.countryCode,
                    }),
                ),
            ]
        }),

        validateCheckout: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "VALIDATING_CHECKOUT")
            const selectedSuiteNumber = ctx.selectedSuiteNumber
            const selectedSuiteCategory = ctx.selectedSuiteCategory
            const fare = fareCardByKey(ctx, ctx.selectedFare?.key)
            if (!selectedSuiteNumber || !selectedSuiteCategory || !fare) return undefined
            return [
                sendTo<Context, Events, ValidateCheckoutCommandTypes["ActorRef"]>(
                    "validateCheckoutCommand",
                    (): ValidateCheckoutCommandTypes["Load"] => ({
                        type: "LOAD",
                        data: {
                            id: CheckoutProcessId.fromUUID(ctx.sessionId),
                            trackingId: ev.trackingData.trackingId,
                            auid: ev.trackingData.auid,
                            captchaSolution: ev.captchaSolution,
                            price: PriceRequestV2.build({
                                cruiseCode: ctx.cruiseCode,
                                fareCode: fare.fare,
                                vsMember: vsMember(ctx.vsCode, fare.options.vs),
                                vsCode: O.fromNullable(validVsCodeValue(ctx.vsCode)),
                                suiteCategory: selectedSuiteCategory,
                                occupancy: ctx.guests,
                                availabilities: ctx.flowVersion.availabilities,
                                air: fare.air,
                            }),
                            suiteNumber: selectedSuiteNumber,
                            mainGuest: ev.mainGuest,
                            otherGuests: ev.otherGuests,
                            suitePayment: ev.suitePayment,
                            flowType: ev.flowType,
                        },
                        country: ctx.geoLocation.countryCode,
                    }),
                ),
            ]
        }),

        loadCheckoutStatus: sendTo<Context, Events, CheckoutStatusLoaderTypes["ActorRef"]>(
            "checkoutStatusLoader",
            (ctx, ev) => ({
                type: "LOAD",
                data: {
                    id: F.pipe(
                        maybeEvent(ev, "LOAD_EXISTING_CHECKOUT"),
                        O.match(
                            () => CheckoutProcessId.fromUUID(ctx.sessionId),
                            (ev) => ev.checkoutId,
                        ),
                    ),
                },
                country: ctx.geoLocation.countryCode,
            }),
        ),

        startPayment: sendTo<Context, Events, StartPaymentCommandTypes["ActorRef"]>(
            "startPaymentCommand",
            (ctx, ev) => {
                assertEvent(ev, "START_PAYMENT")
                return {
                    type: "LOAD",
                    data: {
                        id: ev.paymentId,
                        checkoutProcessId: CheckoutProcessId.fromUUID(ctx.sessionId),
                        trackingId: ev.trackingId,
                        cardType: ev.cardType,
                    },
                    country: ctx.geoLocation.countryCode,
                }
            },
        ),
    },
}
